import { useEffect, useRef, useState } from "react"
import { useGetDiceCurrentRoundQuery, useGetDiceResultsQuery } from "../../services/dice/diceServices"
import "./DiceGame.scss"

const RESULT_MS = 3000

// ── Web Audio ─────────────────────────────────────────────────
let _ctx: AudioContext | null = null

function getCtx(): AudioContext | null {
  try {
    if (!_ctx) _ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
    if (_ctx.state === "suspended") _ctx.resume()
    return _ctx
  } catch { return null }
}

function playTick() {
  const ctx = getCtx()
  if (!ctx) return
  try {
    const len  = Math.floor(ctx.sampleRate * 0.035)
    const buf  = ctx.createBuffer(1, len, ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (len * 0.2))
    const src = ctx.createBufferSource()
    src.buffer = buf
    const bp = ctx.createBiquadFilter()
    bp.type = "bandpass"; bp.frequency.value = 1400; bp.Q.value = 1.2
    const gain = ctx.createGain(); gain.gain.value = 0.5
    src.connect(bp); bp.connect(gain); gain.connect(ctx.destination)
    src.start()
  } catch {}
}

function playChime() {
  const ctx = getCtx()
  if (!ctx) return
  try {
    [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      const t    = ctx.currentTime + i * 0.11
      const osc  = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = "sine"
      osc.frequency.setValueAtTime(freq, t)
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.22, t + 0.015)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45)
      osc.connect(gain); gain.connect(ctx.destination)
      osc.start(t); osc.stop(t + 0.5)
    })
  } catch {}
}

// ── Dot grid positions ─────────────────────────────────────────
const FACE_DOTS: Record<number, number[]> = {
  1: [5],
  2: [3, 7],
  3: [3, 5, 7],
  4: [1, 3, 7, 9],
  5: [1, 3, 5, 7, 9],
  6: [1, 3, 4, 6, 7, 9],
}

const FaceDots = ({ n }: { n: number }) => (
  <div className="cube-face-grid">
    {[1,2,3,4,5,6,7,8,9].map(pos => (
      <div key={pos} className="cube-cell">
        {FACE_DOTS[n]?.includes(pos) && <div className="cube-dot" />}
      </div>
    ))}
  </div>
)

function getDots(n: number): [number, number][] {
  switch (n) {
    case 1: return [[50, 50]]
    case 2: return [[30, 30], [70, 70]]
    case 3: return [[30, 30], [50, 50], [70, 70]]
    case 4: return [[30, 30], [70, 30], [30, 70], [70, 70]]
    case 5: return [[30, 30], [70, 30], [50, 50], [30, 70], [70, 70]]
    case 6: return [[30, 25], [70, 25], [30, 50], [70, 50], [30, 75], [70, 75]]
    default: return []
  }
}

const DiceFaceLarge = ({ num }: { num: number }) => (
  <svg width="90" height="90" viewBox="0 0 100 100" style={{ display: "block", filter: "drop-shadow(0 6px 18px rgba(0,0,0,0.7))" }}>
    <defs>
      <linearGradient id="dfg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#e8eaf4" />
      </linearGradient>
    </defs>
    <rect x="4" y="4" width="92" height="92" rx="16" ry="16" fill="url(#dfg)" stroke="rgba(0,0,0,0.1)" strokeWidth="1.5" />
    <rect x="8" y="8" width="84" height="34" rx="12" ry="12" fill="rgba(255,255,255,0.5)" />
    {getDots(num).map(([cx, cy], i) => (
      <g key={i}>
        <circle cx={cx + 1} cy={cy + 1} r="9" fill="rgba(0,0,0,0.15)" />
        <circle cx={cx} cy={cy} r="9" fill="#16172e" />
      </g>
    ))}
  </svg>
)

const Dice3D = ({ rolling, resultNum, lastResult }: { rolling: boolean; resultNum: number | null; lastResult: number | null }) => {
  if (!rolling) {
    const showNum = resultNum ?? lastResult
    return (
      <div className={`dice-svg-result ${resultNum ? "dice-svg-result--show" : ""}`}>
        {showNum && <DiceFaceLarge num={showNum} />}
      </div>
    )
  }
  return (
    <div className="cube-scene">
      <div className="cube cube--rolling">
        <div className="cube-face cube-face--front"><FaceDots n={1} /></div>
        <div className="cube-face cube-face--back"><FaceDots n={6} /></div>
        <div className="cube-face cube-face--top"><FaceDots n={2} /></div>
        <div className="cube-face cube-face--bottom"><FaceDots n={5} /></div>
        <div className="cube-face cube-face--right"><FaceDots n={3} /></div>
        <div className="cube-face cube-face--left"><FaceDots n={4} /></div>
      </div>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────
const DiceGame = () => {
  const [phase, setPhase]         = useState<"betting" | "rolling" | "result">("betting")
  const [countdown, setCountdown] = useState<number>(0)
  const [resultNum, setResultNum] = useState<number | null>(null)
  const [history, setHistory]     = useState<number[]>([])
  const [roundId, setRoundId]     = useState<string>("")

  const wsRef             = useRef<WebSocket | null>(null)
  const lastRoundIdRef    = useRef<string>("")
  const lastResultRef     = useRef<number | null>(null)
  const resultTimeoutRef  = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unmountedRef      = useRef<boolean>(false)

  const { data: currentRound } = useGetDiceCurrentRoundQuery(undefined, { pollingInterval: 3000 })
  const { data: diceResults }  = useGetDiceResultsQuery(undefined, { pollingInterval: 5000 })

  // Sync from current-round API
  useEffect(() => {
    if (!currentRound?.success) return
    const rd = currentRound.data
    if (rd?.autotime !== undefined) setCountdown(rd.autotime)
    if (rd?.status === "betting") setPhase("betting")
    if (rd?.status === "rolling") setPhase("rolling")
    if (rd?.status === "result" || rd?.status === "settled") {
      if (rd?.result) setResultNum(rd.result)
      setPhase("result")
    }
  }, [currentRound])

  // Sync history from results API
  useEffect(() => {
    if (!diceResults?.success) return
    const list: any[] = diceResults.data ?? []
    if (!Array.isArray(list) || list.length === 0) return

    const settled = list.filter((r: any) => r.status === "settled" && r.result != null)
    if (settled.length === 0) return

    setHistory(settled.map((r: any) => r.result).slice(0, 20))

    const latest = settled[0]
    if (lastResultRef.current === null) {
      lastResultRef.current = latest.result
    }

    if (latest.roundId !== lastRoundIdRef.current) {
      lastRoundIdRef.current = latest.roundId
      lastResultRef.current = latest.result
      setResultNum(latest.result)
      setPhase("result")
      playChime()
      if (resultTimeoutRef.current) clearTimeout(resultTimeoutRef.current)
      resultTimeoutRef.current = setTimeout(() => {
        setResultNum(null)
        setPhase("betting")
      }, RESULT_MS)
    }
  }, [diceResults])

  // WebSocket for live updates
  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || ""
    const wsUrl   = apiBase.replace(/^http(s?)/, "ws$1") + "/api/dice/ws"

    const connect = () => {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data)
          if (msg.roundId) setRoundId(msg.roundId)

          if (msg.type === "newRound") {
            setPhase("betting")
            setResultNum(null)
          }
          if (msg.type === "tick") {
            if (msg.autotime !== undefined) setCountdown(msg.autotime)
            if (msg.betOpen === true) setPhase("betting")
          }
          if (msg.type === "rolling") {
            setPhase("rolling")
            setResultNum(null)
            setCountdown(0)
          }
          if (msg.type === "result") {
            const num = msg.result
            if (num) {
              lastResultRef.current = num
              lastRoundIdRef.current = msg.roundId
              setResultNum(num)
              setPhase("result")
              playChime()
              if (resultTimeoutRef.current) clearTimeout(resultTimeoutRef.current)
              resultTimeoutRef.current = setTimeout(() => {
                setResultNum(null)
                setPhase("betting")
              }, RESULT_MS)
            }
          }
        } catch {}
      }

      ws.onerror = () => {}
      ws.onclose = () => {
        reconnectTimerRef.current = setTimeout(() => {
          if (!unmountedRef.current && wsRef.current?.readyState !== WebSocket.OPEN) connect()
        }, 3000)
      }
    }

    connect()
    return () => {
      unmountedRef.current = true
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      if (resultTimeoutRef.current) clearTimeout(resultTimeoutRef.current)
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.onmessage = null
        wsRef.current.close()
      }
    }
  }, [])

  useEffect(() => {
    if (phase === "rolling") playTick()
  }, [phase])

  return (
    <div className="dice-container">
      {/* Header */}
      <div className="dice-header">
        <div className="dice-header-left">
          Dice Game<span className="dice-rules-link"> | Live</span>
        </div>
        <div className="dice-header-right" title={roundId}>
          {roundId ? roundId.replace("DICE", "#") : `#${history.length}`}
        </div>
      </div>

      {/* Dice 3D display */}
      <div className={`dice-display ${phase === "result" ? "dice-display--result" : ""}`}>
        <div className="dice-display-inner">
          <Dice3D rolling={phase === "rolling"} resultNum={resultNum} lastResult={lastResultRef.current} />
        </div>
        {phase === "betting" && countdown > 0 && (
          <div className={`dice-cd-overlay ${countdown <= 5 ? "dice-cd-overlay--red" : ""}`}>
            <span key={countdown} className="dice-cd-num">{countdown}</span>
          </div>
        )}
      </div>

      {/* Last Results History */}
      <div className="dice-history">
        <div className="dice-history-label">Last Results</div>
        <div className="dice-history-dots">
          {history.map((n, i) => (
            <div key={i} className={`dice-history-dot ${n % 2 === 0 ? "dice-dot-even" : "dice-dot-odd"}`}>
              {n}
            </div>
          ))}
          {history.length === 0 && <span className="dice-no-history">No history yet</span>}
        </div>
      </div>
    </div>
  )
}

export default DiceGame
